import { FLAG_MECHANISM_PREFIXES, FlagCatalogFragment, FlagConfig } from './flag-catalog.types';
import { FlagCatalogError } from './flag-catalog.error';
import { freezeEntry } from './freeze-entry';
import { assertDefaultMembership } from './assert-default-membership';
import { toPhysicalKey } from './to-physical-key';

/**
 * Merge per-context catalog fragments into one immutable `FLAG_CATALOG`.
 *
 * The composition root (not any single context) calls this: it imports every
 * context's fragment and assembles the single source of truth for flag names.
 * The returned object's key type is the UNION of the fragments' key types, so
 * `type FlagKey = keyof typeof FLAG_CATALOG` is the closed, typed union a
 * mistyped key fails to satisfy at compile time.
 *
 * Build-time invariants (these run at module load):
 *
 * 1. **No duplicate keys across fragments.** Two contexts claiming the same
 *    fully-qualified key is a {@link FlagCatalogError} — a flag has exactly one
 *    owner.
 * 2. **Known prefix.** Every key's prefix must be a known mechanism. Fragments
 *    built via `defineFlags` already guarantee this; the re-check defends against
 *    a hand-rolled or untyped fragment reaching `composeCatalog`.
 * 3. **Variant fence.** A `type: 'variant'` flag is rejected on a `wiring.*`
 *    key. `defineFlags` already enforces this, but the error contract documents
 *    the invariant as holding for BOTH factories, so the re-check defends the
 *    composition root against a hand-rolled or untyped `wiring.*` variant
 *    fragment that never passed through `defineFlags`.
 * 4. **Immutable entries.** Every stored entry is deep-frozen (including nested
 *    `enum.values` / `variant.variants`). `defineFlags` already freezes the
 *    entries it mints, but `composeCatalog` also accepts hand-rolled/untyped
 *    fragments whose entries are not frozen; the re-freeze on store keeps a
 *    mutable source fragment from mutating `FLAG_CATALOG` after composition.
 * 5. **Default membership.** An `enum` entry's `defaultValue` must be one of its
 *    `values`, and a `variant` entry's `defaultVariant` one of its `variants`.
 *    `defineFlags` already enforces this, but the re-check (via the shared
 *    `assertDefaultMembership`) defends the composition root against a hand-rolled
 *    or untyped fragment whose default falls outside its declared closed set.
 * 6. **AppConfig-legal key.** Every logical key must encode to a legal AppConfig
 *    physical key (via the shared {@link toPhysicalKey}). This closes the gap
 *    where a catalog with a key that is a valid logical string but encodes to an
 *    illegal/overlong physical key would pass authoring and the build, then fail
 *    only when the AppConfig profile GENERATOR runs. Enforcing the same transform
 *    the generator and adapter use at composition time makes an unprojectable
 *    key a build-time {@link FlagCatalogError}, not a deploy-time surprise.
 *
 * @param fragments the frozen fragments produced by `defineFlags`.
 * @returns a frozen catalog whose keys are the union of all fragment keys.
 */
export function composeCatalog<const Fragments extends readonly FlagCatalogFragment[]>(
    ...fragments: Fragments
): Readonly<UnionToIntersection<Fragments[number]>> {
    const catalog: Record<string, FlagConfig> = {};

    for (const fragment of fragments) {
        for (const [key, config] of Object.entries(fragment)) {
            const prefix = key.split('.', 1)[0];
            if (!(FLAG_MECHANISM_PREFIXES as readonly string[]).includes(prefix)) {
                throw new FlagCatalogError(
                    `Unknown flag mechanism prefix on key '${key}'. ` +
                        `Expected one of: ${FLAG_MECHANISM_PREFIXES.join(', ')}.`
                );
            }

            if (prefix === 'wiring' && config.type === 'variant') {
                throw new FlagCatalogError(
                    `Flag '${key}' declares type 'variant', which is not allowed on 'wiring.*' keys. ` +
                        `A wiring flag selects a port's adapter (a one-way cutover) and must stay binary.`
                );
            }

            assertDefaultMembership(key, config);

            // Enforce the AppConfig-legal physical-key invariant with the SAME
            // shared transform the profile generator and adapter use, so an
            // unprojectable key fails here (build time) rather than only when the
            // generator runs. `toPhysicalKey` throws a FlagCatalogError on an
            // illegal/overlong encoding.
            toPhysicalKey(key);

            if (Object.prototype.hasOwnProperty.call(catalog, key)) {
                throw new FlagCatalogError(
                    `Duplicate flag key '${key}' across fragments. A flag key must be owned by exactly one context.`
                );
            }

            catalog[key] = freezeEntry(config);
        }
    }

    return Object.freeze(catalog) as Readonly<UnionToIntersection<Fragments[number]>>;
}

/**
 * Collapse a union of fragment record types into a single record type whose keys
 * are the union of all fragment keys. This is what makes `keyof typeof
 * FLAG_CATALOG` the full closed key union across every composed fragment.
 */
type UnionToIntersection<U> = (U extends unknown ? (arg: U) => void : never) extends (arg: infer I) => void ? I : never;
